/**
 * Flammable Installation Service
 * API integration for flammable installations (Laudo NR 20), their products, records and the
 * record's file.
 */
import apiClient from './apiClient';
import type { DependencyCheck } from '../types/dependencyCheck';
import {
  FlammableInstallation,
  FlammableInstallationCreateData,
  FlammableInstallationFilters,
  FlammableInstallationFormData,
  FlammableInstallationListResponse,
  FlammableRecord,
  FlammableRecordFormData,
  FlammableSubstance,
  FlammableSubstanceFormData,
} from '../types/flammableInstallation';

export * from '../types/flammableInstallation';

export interface FlammableInstallationListParams extends FlammableInstallationFilters {
  page?: number;
  limit?: number;
}

type Id = string | number;

class FlammableInstallationService {
  private baseUrl = '/flammable-installations';

  async getInstallations(
    params: FlammableInstallationListParams = {}
  ): Promise<FlammableInstallationListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.departmentId !== undefined) queryParams.append('departmentId', String(filters.departmentId));
    if (filters.class) queryParams.append('class', filters.class);
    if (filters.situation) queryParams.append('situation', filters.situation);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);
    const backendData = response.data.data || response.data;

    return {
      installations: backendData.installations ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  async getInstallationById(id: Id): Promise<FlammableInstallation> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  async createInstallation(data: FlammableInstallationCreateData): Promise<FlammableInstallation> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  async updateInstallation(
    id: Id,
    data: Partial<FlammableInstallationFormData>
  ): Promise<FlammableInstallation> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteInstallation(id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /** Quantos produtos e registros impedem a exclusão (`details.substances` e `details.records`). */
  async checkDependencies(id: Id): Promise<DependencyCheck> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  async getSubstances(installationId: Id): Promise<FlammableSubstance[]> {
    const response = await apiClient.get(`${this.baseUrl}/${installationId}/substances`);
    return response.data.data || response.data;
  }

  async createSubstance(installationId: Id, data: FlammableSubstanceFormData): Promise<FlammableSubstance> {
    const response = await apiClient.post(`${this.baseUrl}/${installationId}/substances`, data);
    return response.data.data || response.data;
  }

  async updateSubstance(
    installationId: Id,
    substanceId: Id,
    data: Partial<FlammableSubstanceFormData>
  ): Promise<FlammableSubstance> {
    const response = await apiClient.put(`${this.baseUrl}/${installationId}/substances/${substanceId}`, data);
    return response.data.data || response.data;
  }

  async deleteSubstance(installationId: Id, substanceId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${installationId}/substances/${substanceId}`);
  }

  async getRecords(installationId: Id): Promise<FlammableRecord[]> {
    const response = await apiClient.get(`${this.baseUrl}/${installationId}/records`);
    return response.data.data || response.data;
  }

  async createRecord(installationId: Id, data: FlammableRecordFormData): Promise<FlammableRecord> {
    const response = await apiClient.post(`${this.baseUrl}/${installationId}/records`, data);
    return response.data.data || response.data;
  }

  async updateRecord(
    installationId: Id,
    recordId: Id,
    data: Partial<FlammableRecordFormData>
  ): Promise<FlammableRecord> {
    const response = await apiClient.put(`${this.baseUrl}/${installationId}/records/${recordId}`, data);
    return response.data.data || response.data;
  }

  async deleteRecord(installationId: Id, recordId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${installationId}/records/${recordId}`);
  }

  /** Anexa (ou troca) o arquivo do registro; o servidor aceita PDF, PNG e JPEG de até 9 MB. */
  async uploadRecordFile(installationId: Id, recordId: Id, file: File): Promise<FlammableRecord> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `${this.baseUrl}/${installationId}/records/${recordId}/file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data || response.data;
  }

  async downloadRecordFile(installationId: Id, recordId: Id): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${installationId}/records/${recordId}/file`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  async deleteRecordFile(installationId: Id, recordId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${installationId}/records/${recordId}/file`);
  }
}

export const flammableInstallationService = new FlammableInstallationService();
export default flammableInstallationService;
